import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useId,
  forwardRef,
  useImperativeHandle,
  ReactNode,
} from "react";

export interface PopoverRef {
  close: () => void;
}

const Popover = forwardRef<
  PopoverRef,
  {
    children: ReactNode;
    content: ReactNode;
    placement?: string;
  }
>(({ children, content, placement = "bottom" }, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const id = useId();

  useImperativeHandle(ref, () => ({
    close: () => {
      setIsVisible(false);
    },
  }));

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.target instanceof HTMLElement &&
        !popoverRef.current?.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [id]);

  const classNamePlacement = useMemo(
    () =>
      ({
        bottom: "top-full left-1/2 -translate-x-1/2 before:left-1/2 ",
        bottomLeft: "top-full right-0 before:right-4",
        bottomRight: "top-full left-0",
      })[placement],
    [placement]
  );

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onClick={toggleVisibility}
        aria-haspopup="true"
        aria-expanded={isVisible}
        aria-controls={id}
      >
        {children}
      </div>
      {isVisible && (
        <div
          id={id}
          ref={popoverRef}
          className={`absolute z-50 mt-2.5 transform whitespace-nowrap rounded-md bg-white p-4 shadow before:absolute before:-top-4 before:border-8 before:border-solid before:border-transparent before:border-b-[#5570F114] before:content-[''] focus:outline-none ${classNamePlacement}`}
          role="dialog"
          aria-modal="true"
        >
          {content}
        </div>
      )}
    </div>
  );
});

Popover.displayName = "Popover";

export default Popover;
